import { useEffect, useRef } from "react";

/**
 * NpfEnquiryWidget — NoPaperForms "Enquire Now" form.
 *
 * The vendor snippet is:
 *
 *   <div class="npf_wgts" data-height="400px"
 *        data-w="a32c79525ac1ae71cd2da5c5a5a99417"></div>
 *   <script type="text/javascript" async
 *        src="https://1v4ldzr9l7.in4.widgets.nopaperforms.com/vemwgts.js"></script>
 *
 * vemwgts.js reads `location.href` and builds
 *
 *   /register?&r=<referrer>&q=<query>&w=<widget id>&m=&cu=<location.href>
 *
 * then drops the resulting iframe into the placeholder. NoPaperForms only
 * answers that request for the page URL the widget is mapped to: `cu` with
 * localhost / a LAN IP / a preview origin is answered with "403 Forbidden"
 * (the mapping check is on `cu`, the cookies only drive `r`/`q`). So the
 * widget renders on https://www.ncet.co.in but not on a dev machine.
 *
 * Fix: on the mapped site we run the snippet verbatim. On any other origin we
 * create the vendor's iframe ourselves with `cu`/`r` set to the mapped site, so
 * the form loads locally (and on preview URLs) instead of a 403 box.
 */
const NPF_WIDGET_ID = "a32c79525ac1ae71cd2da5c5a5a99417";
const NPF_ORIGIN = "https://1v4ldzr9l7.in4.widgets.nopaperforms.com";
const NPF_SCRIPT_SRC = `${NPF_ORIGIN}/vemwgts.js`;
const NPF_REGISTER_URL = `${NPF_ORIGIN}/register`;

// The domain the NoPaperForms widget is mapped to. Override with
// REACT_APP_NPF_SITE_URL when the form is moved to another domain.
const NPF_MAPPED_SITE = (
  process.env.REACT_APP_NPF_SITE_URL || "https://www.ncet.co.in"
).replace(/\/+$/, "");

const bareHost = (hostname = "") => hostname.replace(/^www\./i, "");

const hostOf = (url) => {
  try {
    return bareHost(new URL(url).hostname);
  } catch (e) {
    return "";
  }
};

const NPF_MAPPED_HOST = hostOf(NPF_MAPPED_SITE);

const isMappedHost = () =>
  !!NPF_MAPPED_HOST && bareHost(window.location.hostname) === NPF_MAPPED_HOST;

// Same shape vemwgts.js builds, with `cu` (the page URL the vendor validates)
// and `r` (referrer) pointing at the mapped site instead of this origin.
const buildMappedSrc = () => {
  const page = `${window.location.pathname}${window.location.search}`;
  const mappedPage = `${NPF_MAPPED_SITE}${page}`;
  const mappedReferrer = `${NPF_MAPPED_SITE}/`;
  return (
    `${NPF_REGISTER_URL}?&r=${encodeURIComponent(mappedReferrer)}` +
    `&q=&w=${NPF_WIDGET_ID}&m=&cu=${encodeURIComponent(mappedPage)}`
  );
};

const NpfEnquiryWidget = ({ height = "400px" }) => {
  const placeholder = useRef(null);

  useEffect(() => {
    const el = placeholder.current;
    if (!el || el.dataset.npfReady === "true") return undefined;

    // Mapped domain: use the vendor script exactly as the snippet does. It
    // renders into the .npf_wgts placeholder, so it is appended to <body>
    // (the snippet's "before </body>" position), once per page load.
    if (isMappedHost()) {
      el.dataset.npfReady = "true";
      if (window._npfEnquiryWidgetLoaded) return undefined;
      window._npfEnquiryWidgetLoaded = true;

      const s = document.createElement("script");
      s.type = "text/javascript";
      s.async = true;
      s.src = NPF_SCRIPT_SRC;
      s.dataset.npfEnquiryWidget = "true";
      // The vendor script is cross-origin without CORS, so any failure inside it
      // surfaces as an opaque "Script error.". Keep our side defensive: a vendor
      // hiccup must never leave the widget permanently "ready" or throw from here.
      s.onerror = () => {
        console.warn("[npf] enquiry widget script failed to load");
        delete el.dataset.npfReady;
        window._npfEnquiryWidgetLoaded = false;
      };
      try {
        document.body.appendChild(s);
      } catch (err) {
        console.warn("[npf] enquiry widget injection failed", err);
        delete el.dataset.npfReady;
        window._npfEnquiryWidgetLoaded = false;
      }
      return undefined;
    }

    // Any other origin (localhost, LAN IP, preview URL): the same iframe the
    // vendor script would create, requesting the form as the mapped site.
    el.dataset.npfReady = "true";
    el.innerHTML = "";

    const iframe = document.createElement("iframe");
    iframe.frameBorder = "0";
    iframe.width = "100%";
    iframe.height = height;
    iframe.title = "Enquire Now form";
    // The vendor returns an empty page (200, no body) when the request has no
    // Referer, so the iframe must send at least the origin.
    iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
    iframe.setAttribute(
      "sandbox",
      "allow-top-navigation allow-scripts allow-same-origin allow-downloads allow-popups allow-popups-to-escape-sandbox"
    );
    iframe.setAttribute("src", buildMappedSrc());
    el.appendChild(iframe);

    return () => {
      el.innerHTML = "";
      delete el.dataset.npfReady;
    };
  }, [height]);

  return <div ref={placeholder} className="npf_wgts" data-height={height} data-w={NPF_WIDGET_ID} />;
};

export default NpfEnquiryWidget;
